import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronsLeftRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LiquidPageHeader } from "@/components/liquid/LiquidPageHeader";
import { ZoomParallax } from "@/components/liquid/ZoomParallax";
import { VideoBackdrop } from "@/components/liquid/VideoBackdrop";
import { ParallaxY } from "@/components/liquid/ParallaxY";
import { pageVideo } from "@/lib/videos";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { useContent } from "@/context/ContentContext";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function GalleryLiquid() {
  const { content } = useContent();
  const items = content?.gallery || [];
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const vis = (k) => content?.settings?.section_visibility?.[k] ?? true;

  const categories = useMemo(() => ["All", ...new Set(items.map((g) => g.category))], [items]);
  const filtered = filter === "All" ? items : items.filter((g) => g.category === filter);
  const comparisons = filtered.filter((g) => g.before_image && g.after_image);
  const photos = filtered.filter((g) => !(g.before_image && g.after_image));

  return (
    <div className="relative z-[1] px-4 pb-24 pt-36 sm:px-8 sm:pt-44 lg:px-16" data-testid="gallery-liquid">
      <VideoBackdrop src={pageVideo(content?.settings, "gallery")} testid="gallery-video-bg" />
      <div className="mx-auto max-w-6xl">
        <LiquidPageHeader
          index="04"
          eyebrow="The Results Gallery"
          title="Proof, Not Promises"
          subtitle="Drag the gold handle on any comparison to reveal the transformation."
          testid="gallery-title"
        />

        <Reveal delay={0.3} className="mt-10">
          <div className="flex flex-wrap gap-x-8 gap-y-3" role="tablist" aria-label="Gallery categories">
            {categories.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={filter === c}
                onClick={() => setFilter(c)}
                data-testid={`gallery-tab-${slug(c)}`}
                className={`border-b-2 pb-2 text-xs font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                  filter === c ? "border-[#D4AF37] text-[#1A1A1A]" : "border-transparent text-[#1A1A1A]/40 hover:text-[#B38B2D]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {comparisons.length > 0 && vis("gallery_before_after") && (
          <div className="mt-14">
            <Reveal>
              <div className="flex items-center gap-3">
                <ChevronsLeftRight className="h-4 w-4 text-[#B38B2D]" aria-hidden="true" />
                <h2 className="font-display text-3xl font-normal text-[#1A1A1A]">Before &amp; After</h2>
                <span className="text-xs font-light text-[#1A1A1A]/40">— drag to compare</span>
              </div>
            </Reveal>
            <div className="mt-8 grid gap-10 md:grid-cols-2" data-testid="before-after-grid">
              {comparisons.map((g, i) => (
                <Reveal key={g.id} delay={(i % 2) * 0.1}>
                  <figure data-testid={`gallery-comparison-${g.id}`}>
                    <BeforeAfterSlider before={g.before_image} after={g.after_image} title={g.title} />
                    <figcaption className="mt-4">
                      <h3 className="font-display text-2xl font-normal text-[#1A1A1A]">{g.title}</h3>
                      <p className="mt-1.5 text-sm font-light text-[#5A504B]">{g.description}</p>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {photos.length > 0 && vis("gallery_photos") && (
          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
            {photos.map((g, i) => (
              <Reveal key={g.id} delay={(i % 3) * 0.08}>
                <ParallaxY speed={[0, 0.55, 1.1][i % 3]}>
                  <button onClick={() => setLightbox(g)} data-testid={`gallery-item-${g.id}`} className="group block w-full text-left focus:outline-none focus:ring-2 focus:ring-[#D4AF37]">
                    <ZoomParallax src={g.image} alt={g.title} className="aspect-[3/4] rounded-[4px]" />
                    <p className="mt-3 text-[10px] font-medium uppercase tracking-[0.2em] text-[#1A1A1A]/50 transition-colors group-hover:text-[#B38B2D]">{g.title}</p>
                  </button>
                </ParallaxY>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/60 bg-[#FFFDF9]/95 backdrop-blur-xl sm:max-w-2xl" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{lightbox?.title || "Gallery image"}</DialogTitle>
          {lightbox && (
            <div>
              <img src={lightbox.image} alt={lightbox.title} className="w-full rounded-[4px] object-cover" />
              <p className="eyebrow mt-5">{lightbox.category}</p>
              <h3 className="mt-2 font-display text-3xl font-normal text-[#1A1A1A]">{lightbox.title}</h3>
              <p className="mt-2 text-sm font-light leading-relaxed text-[#5A504B]">{lightbox.description}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
